import React, { useMemo } from 'react';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { Badge, InlineEditRowActions, Table, TextLink, Tooltip } from '@shared/components';
import { useLatestRef } from '@shared/hooks/useLatestRef';
import { PermissionGuard } from '@/features/auth';
import { SquarePenIcon, Trash2Icon } from 'lucide-react';
import type { SageMappingRowEditor } from '../../hooks/useSageMappingRowEditor';
import type { SageNominal } from '../../types/dealerSageMappings.types';
import { sageRowSubject, type SageTableRow } from '../../utils/sageMappingRows';
import { shortId } from '../../utils/shortId';
import { SageNominalDraftInput } from './SageNominalDraftFields';

const EDIT_PERMISSION = { resource: 'ledger', action: 'sage-mapping.edit' } as const;

type EditorControls = Pick<
    SageMappingRowEditor,
    'editingKey' | 'isSaving' | 'drafts' | 'beginEdit' | 'cancelEdit' | 'save' | 'requestRemoval'
>;

interface Props {
    rows: SageTableRow[];
    editor: EditorControls;
}

interface TableMeta {
    isPending?: boolean;
}

const isPending = (info: CellContext<SageTableRow, unknown>): boolean =>
    !!(info.table.options.meta as TableMeta | undefined)?.isPending;

const Dash = () => <span className="text-sm text-mp-muted">—</span>;

function ProductCell({ row }: { row: SageTableRow }) {
    const { productId, productName, entitled } = row.product;
    return (
        <span className="inline-flex items-center gap-2 text-sm">
            {productName ? (
                <span className="font-semibold">{productName}</span>
            ) : (
                <span className="font-semibold" title={productId}>Unknown product {shortId(productId)}</span>
            )}
            {!entitled && <Badge variant="warning">No longer offered</Badge>}
        </span>
    );
}

function StatusCell({ row }: { row: SageTableRow }) {
    return row.nominal ? <Badge variant="success">Mapped</Badge> : <Badge variant="warning">Unmapped</Badge>;
}

export const DealerSageMappingDataTable: React.FC<Props> = ({ rows, editor }) => {
    const editorRef = useLatestRef(editor);
    const { editingKey } = editor;

    const columns = useMemo<ColumnDef<SageTableRow>[]>(() => {
        const editingRow = (row: SageTableRow) => editingKey === row.key;
        const valueCell = (render: (nominal: SageNominal) => React.ReactNode, field: 'code' | 'name' | 'date', autoFocus = false) =>
            (info: CellContext<SageTableRow, unknown>) => {
                const row = info.row.original;
                if (editingRow(row)) {
                    return (
                        <SageNominalDraftInput
                            field={field}
                            rowKey={row.key}
                            subject={sageRowSubject(row)}
                            drafts={editorRef.current.drafts}
                            disabled={isPending(info)}
                            autoFocus={autoFocus}
                        />
                    );
                }
                return row.nominal ? render(row.nominal) : <Dash />;
            };

        return [
            { id: 'product', header: 'Product', cell: (info) => <ProductCell row={info.row.original} /> },
            {
                id: 'nominal_code',
                header: 'Account code',
                cell: valueCell((n) => <span className="font-mono text-sm">{n.nominalCode}</span>, 'code', true),
            },
            {
                id: 'nominal_name',
                header: 'Account name',
                cell: valueCell((n) => (n.nominalName ? <span className="text-sm">{n.nominalName}</span> : <Dash />), 'name'),
            },
            {
                id: 'effective_from',
                header: 'Effective from',
                cell: valueCell((n) => <span className="text-sm">{n.effectiveFrom}</span>, 'date'),
            },
            {
                id: 'status',
                header: 'Status',
                cell: (info) => <StatusCell row={info.row.original} />,
            },
            {
                id: 'actions',
                header: 'Actions',
                enableSorting: false,
                cell: (info) => {
                    const row = info.row.original;
                    const subject = sageRowSubject(row);
                    if (editingRow(row)) {
                        return (
                            <InlineEditRowActions
                                isPending={isPending(info)}
                                onAccept={() => void editorRef.current.save()}
                                onCancel={() => editorRef.current.cancelEdit()}
                                acceptLabel={`Save Sage account code for ${subject}`}
                                cancelLabel={`Cancel editing Sage account code for ${subject}`}
                                acceptPermission={EDIT_PERMISSION}
                            />
                        );
                    }
                    const target = { productId: row.productId, nominal: row.nominal, label: subject };
                    const locked = editingKey !== null;
                    return (
                        <PermissionGuard permission={EDIT_PERMISSION}>
                            <div className="flex gap-2" data-row-actions={row.key}>
                                {row.product.entitled && (
                                    <Tooltip content={`Edit Sage account code for ${subject}`}>
                                        <TextLink
                                            color="secondary"
                                            ariaLabel={`Edit Sage account code for ${subject}`}
                                            startIcon={<SquarePenIcon size={18} />}
                                            disabled={locked}
                                            onClick={() => editorRef.current.beginEdit(target)}
                                        />
                                    </Tooltip>
                                )}
                                {row.nominal && (
                                    <Tooltip content={`Remove Sage account code for ${subject}`}>
                                        <TextLink
                                            color="error"
                                            ariaLabel={`Remove Sage account code for ${subject}`}
                                            startIcon={<Trash2Icon size={18} />}
                                            disabled={locked}
                                            onClick={() => editorRef.current.requestRemoval(target)}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        </PermissionGuard>
                    );
                },
            },
        ];
    }, [editingKey, editorRef]);

    return (
        <Table<SageTableRow>
            data={rows}
            columns={columns}
            enableSorting={false}
            getRowId={(row) => row.key}
            meta={{ isPending: editor.isSaving }}
        />
    );
};
